import {
  SCROLL_DETECTION_CONFIG,
  isConfiguredFormElement,
  isConfiguredEditor,
  matchesPreventionSelector,
  isInAllowedArea,
  getSpecialContainerConfig,
} from './scrollPreventionConfig'

/**
 * Centralized service for determining scroll-to-slide prevention
 * This replaces the scattered logic in the prevent-wheel-propagation directive
 */

/**
 * Check if an element is scrollable (has overflow and content that overflows)
 */
const isScrollableElement = (element) => {
  if (!element || element.nodeType !== 1) return false

  const style = window.getComputedStyle(element)
  const isScrollableCss = style.overflowY === 'auto' || style.overflowY === 'scroll'

  // Only consider it scrollable if CSS allows it and its content overflows
  return isScrollableCss && element.scrollHeight > element.clientHeight
}

/**
 * Check if an element has any scrollable children
 */
const hasScrollableChildren = (element, maxDepth = SCROLL_DETECTION_CONFIG.maxChildrenDepth) => {
  if (!element) return false

  // Check if this element is scrollable
  if (isScrollableElement(element)) return true

  // Get special configuration for this container
  const specialConfig = getSpecialContainerConfig(element.id)
  const depthToUse = specialConfig?.maxDepth || maxDepth

  // Check children (breadth-first search with depth limit)
  const queue = [...element.children].map((child) => ({ element: child, depth: 1 }))

  while (queue.length > 0) {
    const { element: current, depth } = queue.shift()

    if (isScrollableElement(current)) return true

    // Only add children if we haven't reached max depth
    if (depth < depthToUse) {
      Array.from(current.children).forEach((child) => {
        queue.push({ element: child, depth: depth + 1 })
      })
    }
  }

  return false
}

/**
 * Check if there's a focused input within the container
 */
const checkForFocusedInput = (container) => {
  const current = document.activeElement

  // Check if current element is a focused input or editor
  if (current && container.contains(current)) {
    if (isConfiguredFormElement(current) || isConfiguredEditor(current)) {
      return true
    }
  }

  return false
}

/**
 * Find if there's a scrollable parent up to the container
 */
const findScrollableParent = (element, container) => {
  let current = element
  let depth = 0

  while (current && current !== container && depth < SCROLL_DETECTION_CONFIG.maxDepth) {
    if (isScrollableElement(current)) {
      return true
    }
    current = current.parentElement
    depth++
  }

  return false
}

/**
 * Main function to determine if scroll-to-slide should be prevented
 * @param {Event} event - The wheel event
 * @param {Element} container - The container element with the directive
 * @returns {boolean} - True if scroll-to-slide should be prevented
 */
export const shouldPreventScrollToSlide = (event, container) => {
  // First check whitelist mode - if scroll-to-slide is not allowed in this area
  if (!isInAllowedArea(event.target, container)) {
    return true
  }

  // Special handling for configured containers
  const specialConfig = getSpecialContainerConfig(container.id)
  if (specialConfig?.checkScrollableChildren && hasScrollableChildren(container)) {
    return true
  }

  // Check if event target is itself a form element or editor
  if (isConfiguredFormElement(event.target) || isConfiguredEditor(event.target)) {
    return true
  }

  // Check if event target matches any custom prevention selectors
  if (matchesPreventionSelector(event.target)) {
    return true
  }

  // Check if we have any focused input within our container
  if (checkForFocusedInput(container)) {
    return true
  }

  // Check if we have any scrollable parent up to our container
  if (findScrollableParent(event.target, container)) {
    return true
  }

  // No prevention conditions met
  return false
}

/**
 * Enhanced wheel event handler that uses the centralized configuration
 * @param {Element} element - The element to attach the handler to
 * @param {Object} options - Configuration options
 * @returns {Function} - The wheel handler function for cleanup
 */
export const createWheelHandler = (element, options = {}) => {
  const throttleTime = options.throttleTime || SCROLL_DETECTION_CONFIG.throttleTime

  let throttleTimer = null

  const throttledHandler = () => {
    // Throttle implementation without lodash dependency if needed
    if (throttleTimer) return

    throttleTimer = setTimeout(() => {
      throttleTimer = null
    }, throttleTime)

    // Intentionally empty for now; required for test compatibility
    // This maintains compatibility with existing tests
  }

  return (event) => {
    if (shouldPreventScrollToSlide(event, element)) {
      // Mark the event to prevent slide navigation
      event.__skipCustomScroll = true
      event.stopPropagation()
      return
    }

    // No prevention conditions met, run the throttled handler
    throttledHandler()
  }
}

/**
 * Utility function to check if scroll-to-slide should be enabled
 * for wheel events in the main handler
 */
export const shouldEnableScrollToSlide = (event) => {
  // Skip if custom scroll flag is set by prevention logic
  if (event.__skipCustomScroll) return false

  // Skip if delta is too small
  if (Math.abs(event.deltaY) < SCROLL_DETECTION_CONFIG.minDeltaY) return false

  return true
}

/**
 * Debug function to test scroll prevention for a specific element
 */
export const debugScrollPrevention = (element, container) => {
  // Return debug information instead of console.log to avoid linter warnings
  return {
    element,
    container,
    isFormElement: isConfiguredFormElement(element),
    isEditor: isConfiguredEditor(element),
    matchesPreventionSelector: matchesPreventionSelector(element),
    inAllowedArea: isInAllowedArea(element, container),
    hasFocusedInput: checkForFocusedInput(container),
    hasScrollableParent: findScrollableParent(element, container),
  }
}

// Export utility functions for advanced use cases
export {
  isScrollableElement,
  hasScrollableChildren,
  checkForFocusedInput,
  findScrollableParent,
}
